import { MaterialCommunityIcons } from '@expo/vector-icons';
import { IssueType, useFindByQuery } from '@gripp/shared-logic';
import {
  Colors,
  IssueHeaderProps,
  IssueMenu,
  Layout,
  severityToVariant,
  SlideAction,
  TypeDropdown,
  TypeVariant,
  useSlideLayout,
  variantToSeverity,
} from '@gripp/shared-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

export const IssueHeaderCropCircle: React.FC<IssueHeaderProps> = (
  props: IssueHeaderProps
) => {
  const { onSlideChange } = useSlideLayout();
  const navigate = useNavigate();
  const issue = props.issue;

  const issueId = issue.id;

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': issueId },
    };
  }, [issueId]);

  const { data: workflows } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });

  const hasReminder = useMemo(() => workflows?.items?.length > 0, [workflows]);

  const [currentSeverity, setSeverity] = useState(issue.severity);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine
    );
  };

  const handleBackPress = async () => {
    await onSlideChange(SlideAction.SINGLE);
    if (props.onBackPress) {
      props.onBackPress();
    } else {
      navigate(-1);
    }
  };

  if (!issue) {
    return;
  }

  return (
    <View style={styles.headerContainer}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable style={styles.backButton} onPress={handleBackPress}>
            <MaterialCommunityIcons
              name="arrow-left"
              size={24}
              color={Colors.black}
            />
          </Pressable>
          <Text style={styles.headerTitleText}>{issue.title}</Text>
        </View>
        <View style={styles.headerActions}>
          <View style={styles.rightSection}>
            {!props.hideMenu && (
              <View style={styles.menuContainer}>
                {issue.isPinned && (
                  <MaterialCommunityIcons
                    name="pin"
                    color={Colors.black}
                    size={20}
                    style={styles.pinnedIcon}
                  />
                )}
                <IssueMenu
                  issue={issue}
                  hasReminder={hasReminder}
                  onIssueDelete={props.onIssueDelete}
                  onPrint={props.onPrint}
                />
              </View>
            )}
          </View>
        </View>
      </View>
      <View style={styles.typeDropdwnContainer}>
        <TypeDropdown
          value={severityToVariant[currentSeverity]}
          onChange={handleSeverityChange}
          disabled={isSeverityDisabled()}
          testID="issue-detail-severity-dropdown"
          availableTypes={['urgent', 'pending', 'solved']}
        />
      </View>
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    zIndex: 1,
  },
  typeDropdwnContainer: {
    ...Layout.container,
    position: 'absolute',
    left: '50%',
    bottom: -50,
    transform: [{ translateX: '-50%' }],
    zIndex: 100,
    borderRadius: 12,
    overflow: 'visible',
    minHeight: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.black,
    marginBottom: 4,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    height: '100%',
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 32,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
  headerContainer: {
    backgroundColor: Colors.primaryGrayBackground,
    position: 'relative',
    overflow: 'visible',
    zIndex: 2,
  },
  header: {
    borderRadius: 4,
    marginBottom: 2,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitleText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.black,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginRight: 4,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;
